import { CommonModule } from '@angular/common';
import type { TemplateRef } from '@angular/core';
import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  output,
  ViewChild,
} from '@angular/core';
import { RouterLink } from '@angular/router';

import { MomentModule } from 'ngx-moment';

import type { RatingScale } from '@core/candidate-evaluation-settings-types';
import type { DisqualifyReason } from '@core/disqualify-reasons-types';
import { EvaluationInsight } from '@core/evaluation-insights-types';
import type { AggregatedEvaluationsScore } from '@core/evaluations-types';
import { CandidateRatingsScoreChartComponent } from '@core/evaluations-ui';
import { TranslatePipe } from '@core/i18n-legacy';
import { HiddenResultPreview, InterviewResult } from '@core/interview-results-types';
import { OfferStatus } from '@core/offers-types';
import { OfferStatusIndicatorComponent } from '@core/offers-ui';
import type { Placement } from '@core/placements-types';
import type { RequisitionOpeningIssue } from '@core/requisitions-types';
import type { Stage } from '@core/stages-types';
import { StageGroup } from '@core/stages-types';
import { Theme } from '@core/theme-util';
import { DateComponent } from '@core/time-ui';
import {
  CollapsibleCardComponent,
  ConnectedOverlayDirective,
  DividerComponent,
  HeadingComponent,
  IconButtonComponent,
  IconComponent,
  OverlayOriginDirective,
  OverlayPosition,
  OverlayPositionPairs,
  RtTooltip,
  SpinnerComponent,
  TruncateComponent,
} from '@core/user-interface-legacy';
import { WalkthroughStepOriginDirective } from '@core/walkthrough-ui';

import { CandidateWorkLocationsButtonComponent } from '../candidate-preferred-locations-button/candidate-work-locations-button.component';
import type {
  DisqualifyEvent,
  PlacementActions,
  PlacementEvent,
  ProceedEvent,
  ShowAutomatedActionDetailsEvent,
} from '../candidate-profile-placements/types';
import { CollapsibleCardAnalyticsDirective } from '../collapsible-card/collapsible-card-analytics.directive';
import { OverdueCandidateBadgeComponent } from '../overdue-candidate-badge/overdue-candidate-badge.component';
import { ActionButtonsComponent } from './action-buttons/action-buttons.component';
import { PlacementEvaluationMeterComponent } from './evaluation-meter/placement-evaluation-meter.component';
import { getPlacementRatingStats } from './evaluation-meter/rating-stats';
import { PlacementRowComponent } from './placement-row/placement-row.component';
import { StageSelectComponent } from './stage-select/stage-select.component';

@Component({
  selector: 'rt-candidate-profile-placement',
  templateUrl: './candidate-profile-placement.component.html',
  styleUrls: ['./candidate-profile-placement.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    CollapsibleCardComponent,
    RtTooltip,
    OfferStatusIndicatorComponent,
    HeadingComponent,
    TruncateComponent,
    IconButtonComponent,
    OverlayOriginDirective,
    ConnectedOverlayDirective,
    PlacementRowComponent,
    IconComponent,
    DividerComponent,
    MomentModule,
    DateComponent,
    TranslatePipe,
    ActionButtonsComponent,
    StageSelectComponent,
    SpinnerComponent,
    OverdueCandidateBadgeComponent,
    CandidateWorkLocationsButtonComponent,
    WalkthroughStepOriginDirective,
    CollapsibleCardAnalyticsDirective,
    RouterLink,
    PlacementEvaluationMeterComponent,
    CandidateRatingsScoreChartComponent,
  ],
})
export class CandidateProfilePlacementComponent {
  public readonly StageGroup = StageGroup;
  public readonly dropdownPosition = [
    OverlayPositionPairs[OverlayPosition.BottomRight],
    OverlayPositionPairs[OverlayPosition.TopRight],
  ];

  public readonly placement = input.required<Placement>();

  public readonly placementActions = input.required<PlacementActions>();

  public readonly stages = input.required<Stage[]>();
  public readonly stageAutomationsFetchHandler = input<(pipelineTemplateId: number) => void>();

  public readonly disqualifyReasons = input.required<DisqualifyReason[]>();

  public readonly isLoading = input<boolean>(false);

  public readonly openingIssue = input<RequisitionOpeningIssue>();
  public readonly showJobFitScore = input<boolean>(true);

  public readonly interviewResults = input.required<Record<number, InterviewResult[]>>();

  public readonly hiddenResultPreview = input.required<HiddenResultPreview | undefined>();
  public readonly evaluationInsights = input.required<EvaluationInsight[]>();
  public readonly evaluationScale = input<RatingScale | null>(null);
  public readonly hasEvaluationImprovements = input<boolean>(false);
  public readonly theme = input<Theme | null>(null);
  public readonly disqualifiedTooltipTemplate = input.required<TemplateRef<null>>();
  public readonly offerTitleTooltipTemplate = input<TemplateRef<null> | null>(null);

  public readonly moreDropdownTemplate = input<TemplateRef<{
    connectedOverlay: ConnectedOverlayDirective;
  }> | null>(null);

  public readonly collapsible = input(false, { transform: booleanAttribute });

  public readonly isFirst = input(true, { transform: booleanAttribute });

  public readonly isLast = input(true, { transform: booleanAttribute });

  public readonly hasDragHandle = input(false, { transform: booleanAttribute });

  public readonly uniqKey = computed(
    () =>
      `candidate-profile-placement:candidate:${this.placement().candidateId}:job:${
        this.placement().offer?.id
      }:placement:${this.placement().id}`,
  );

  public readonly canOpenMoreDropdown = computed(
    () => this.moreDropdownTemplate() && this.placementActions().openMoreDropdown,
  );

  /**
   * Emits the placement id.
   */
  public editHiringDetailsClick = output<PlacementEvent>();

  public proceedClick = output<ProceedEvent>();

  public disqualifyReasonSelect = output<DisqualifyEvent>();

  public requalifyClick = output<PlacementEvent>();

  public showAutomatedActionDetailsClick = output<ShowAutomatedActionDetailsEvent>();

  public editDisqualifyReasonsClick = output<void>();

  public editWorkLocation = output<PlacementEvent>();

  public disqualifyReasonsActionsDetails = output<DisqualifyReason>();

  public evaluationMeterClick = output<Placement['id']>();

  public previewJobClick = output<PlacementEvent>();

  protected isRatingHidden = computed(
    () => this.placement().ratingVisible !== undefined && !this.placement().ratingVisible,
  );

  protected scoreForChart = computed<AggregatedEvaluationsScore>(() => {
    const rating = this.placement().positiveRatings ?? null;
    const { evalCount } = getPlacementRatingStats(this.placement(), this.interviewResults());

    return {
      average: rating ?? 0,
      ratings:
        rating === null || evalCount === 0
          ? []
          : [{ score: 0, answerCount: evalCount, reviewers: [] }],
    };
  });

  protected offerUrl = computed(() => `/offers/${this.placement().offer?.slug}/pipeline`);

  protected canPreviewJob = computed(() => {
    if (!this.hasEvaluationImprovements()) {
      return false;
    }

    const status = this.placement().offer?.status;
    return status === OfferStatus.Published || status === OfferStatus.Internal;
  });

  protected locationIds = computed(() =>
    this.placement().workLocationId
      ? [this.placement().workLocationId!]
      : this.placement().locationIds || [],
  );

  @ViewChild('moreDropdown')
  private moreDropdown: ConnectedOverlayDirective;

  public onMoreClick($event: MouseEvent): void {
    $event.stopImmediatePropagation();
    this.moreDropdown.toggleOverlay(!this.moreDropdown.open);
  }

  public onPreviewJobClick($event: MouseEvent): void {
    $event.stopImmediatePropagation();
    this.previewJobClick.emit({ placement: this.placement() });
  }
}
